import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User } from 'firebase/auth';
import { 
  Description as DescriptionIcon,
  Work as WorkIcon,
  Settings as SettingsIcon,
  UploadFile as UploadFileIcon,
  Assessment as AssessmentIcon,
  AutoFixHigh as AutoFixHighIcon,
  Business as BusinessIcon,
  Download as DownloadIcon,
  Logout as LogoutIcon,
  History as HistoryIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ViewKanban as ViewKanbanIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { 
  AppBar, 
  Box, 
  CssBaseline, 
  Drawer, 
  IconButton, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Toolbar, 
  Typography,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Avatar,
  Divider,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Stack,
  Chip,
  LinearProgress,
  InputAdornment
} from '@mui/material';
import { analyzeResume, rewriteResume, uploadResume } from '../services/api';
import { signOut, getCurrentUser } from '../services/auth';
import { ResumeAnalysis as ResumeAnalysisType, RewriteResponse } from '../types';
import GenerateExperience from './GenerateExperience';
import JobSearch from './JobSearch';
import CoverLetterGenerator from './CoverLetterGenerator';
import Settings from './Settings';
import ResumeHistory from './ResumeHistory';
import Feedback from './Feedback';
import JobManagement from './JobManagement';

const drawerWidth = 280;

const Dashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [jobDescription, setJobDescription] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [analysis, setAnalysis] = useState<ResumeAnalysisType | null>(null);
  const [optimizedResume, setOptimizedResume] = useState<RewriteResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const analysisRef = useRef<HTMLDivElement>(null);
  const optimizedResumeRef = useRef<HTMLDivElement>(null);
  const [showOptimizedAlert, setShowOptimizedAlert] = useState(false);
  const [showCopyAlert, setShowCopyAlert] = useState(false);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

  useEffect(() => {
    getCurrentUser().then(setUser);
  }, []);

  useEffect(() => {
    if (analysis && analysisRef.current) {
      analysisRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [analysis]);

  useEffect(() => {
    if (optimizedResume && optimizedResumeRef.current) {
      optimizedResumeRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [optimizedResume]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = '/login';
    } catch (error) {
      setError('Failed to sign out');
      console.error('Sign out error:', error);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setError('File size exceeds 10MB limit. Please choose a smaller file.');
        event.target.value = ''; // Clear the file input
        return;
      }
      setResumeFile(file);
      setError(null);
    }
  };

  const handleAnalyze = async () => {
    if (!resumeFile || !jobDescription || !companyName || !jobTitle) {
      setError('Please upload a resume, provide a job description, company name, and job title');
      return;
    }

    setLoading(true);
    setError(null);
    setOptimizedResume(null);

    try {
      const uploadResponse = await uploadResume(resumeFile);
      const result = await analyzeResume(resumeFile, jobDescription, jobTitle);
      setAnalysis(result);
      setTimeout(() => {
        analysisRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleRewrite = async () => {
    if (!analysis) {
      setError('Please analyze your resume first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await rewriteResume(
        analysis.resume_text,
        jobDescription,
        analysis.suggestions,
        analysis.improvements,
        companyName,
        jobTitle,
        jobTitle
      );
      setOptimizedResume(response);
      setShowOptimizedAlert(true);
      setTimeout(() => {
        optimizedResumeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      console.error('Error during rewrite:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during rewrite');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!optimizedResume?.pdf_content) {
      setError('No PDF content available');
      return;
    }

    try {
      const binaryString = window.atob(optimizedResume.pdf_content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      const sanitizedCompanyName = companyName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      link.download = `${sanitizedCompanyName}_optimized_resume.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF processing error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError('Failed to process PDF: ' + errorMessage);
    }
  };

  const handleCopyResume = () => {
    if (optimizedResume?.rewritten_resume) {
      navigator.clipboard.writeText(optimizedResume.rewritten_resume)
        .then(() => {
          setShowCopyAlert(true);
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err);
          setError('Failed to copy resume text');
        });
    }
  };

  const menuItems = [
    { text: 'Resume Analysis', icon: <DescriptionIcon />, path: '/dashboard' },
    { text: 'Generate Experience', icon: <WorkIcon />, path: '/experience' },
    { text: 'Job Search', icon: <WorkIcon />, path: '/jobs' },
    { text: 'Job Management', icon: <ViewKanbanIcon />, path: '/job-management' },
    { text: 'Cover Letter', icon: <DescriptionIcon />, path: '/cover-letter' },
    { text: 'Resume History', icon: <HistoryIcon />, path: '/resume-history' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    { text: 'Feedback & Reviews', icon: <AssessmentIcon />, path: '/feedback' },
  ];

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: 'background.paper',
      borderRight: '1px solid',
      borderColor: 'divider'
    }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Avatar sx={{ bgcolor: 'primary.main' }}>
          <PersonIcon />
        </Avatar>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {user?.email?.split('@')[0] || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email || ''}
        </Typography>
        </Box>
      </Box>

      <List sx={{ px: 2, py: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                borderRadius: 1,
                '&.Mui-selected': {
                  bgcolor: 'primary.lighter',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.lighter',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                },
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  minWidth: 40,
                  color: location.pathname === item.path ? 'primary.main' : 'text.secondary'
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: location.pathname === item.path ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box sx={{ mt: 'auto', p: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <ListItemButton 
          onClick={handleSignOut} 
          sx={{ 
            borderRadius: 1,
            color: 'error.main',
            '&:hover': {
              bgcolor: 'error.lighter',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'error.main', minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Sign Out" 
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { sm: 'none' },
              color: 'text.primary'
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ color: 'text.primary' }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
          >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
        }}
      >
        <Grid container spacing={3}>
          {location.pathname === '/dashboard' && (
            <>
              {/* Upload Section */}
              <Grid item xs={12}>
                <Card elevation={0} sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden'
                }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                      <CloudUploadIcon color="primary" />
                      <Typography variant="h5" fontWeight="600">
                        Upload Your Resume
                      </Typography>
                    </Stack>

                    {error && (
                      <Alert 
                        severity="error" 
                        sx={{ mb: 3 }}
                        onClose={() => setError(null)}
                      >
                        {error}
                      </Alert>
                    )}

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Company Name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Enter company name..."
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BusinessIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Job Title"
                      value={jobTitle}
                      onChange={(e) => setJobTitle(e.target.value)}
                      placeholder="Enter job title..."
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <WorkIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 3,
                            border: '2px dashed',
                            borderColor: error ? 'error.main' : 'divider',
                            borderRadius: 2,
                            textAlign: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                              borderColor: error ? 'error.main' : 'primary.main',
                              bgcolor: 'action.hover',
                            },
                          }}
                          onClick={() => document.getElementById('resume-file')?.click()}
                        >
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                            style={{ display: 'none' }}
                      id="resume-file"
                    />
                          <Stack spacing={2} alignItems="center">
                            <CloudUploadIcon sx={{ 
                              fontSize: 40, 
                              color: error ? 'error.main' : 'primary.main' 
                            }} />
                            <Typography variant="h6" color={error ? 'error' : 'inherit'}>
                              {resumeFile ? resumeFile.name : 'Choose Resume File'}
                            </Typography>
                            <Typography variant="body2" color={error ? 'error' : 'text.secondary'}>
                              Supports PDF, DOC, DOCX (max 10MB)
                            </Typography>
                            {error && (
                              <Typography variant="caption" color="error">
                                {error}
                              </Typography>
                            )}
                          </Stack>
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Job Description"
                      value={jobDescription}
                      onChange={(e) => setJobDescription(e.target.value)}
                      placeholder="Paste the job description here..."
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                    onClick={handleAnalyze} 
                    disabled={loading || !resumeFile || !jobDescription || !companyName || !jobTitle}
                          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AssessmentIcon />}
                          sx={{ py: 1.5 }}
                        >
                          {loading ? 'Generating ATS Review...' : 'Generate ATS Review'}
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Analysis Section */}
              {analysis && (
                <Grid item xs={12} ref={analysisRef}>
                  <Card elevation={0} sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                        <AssessmentIcon color="primary" />
                        <Typography variant="h5" fontWeight="600">
                          Analysis Results
                        </Typography>
                      </Stack>

                      <Box sx={{ mb: 3 }}>
                        <Stack spacing={2}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Box
                              sx={{
                                width: 120,
                                height: 120,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                background: `conic-gradient(
                                  ${analysis.score >= 0.7 ? theme.palette.success.main : 
                                    analysis.score >= 0.4 ? theme.palette.warning.main : 
                                    theme.palette.error.main} 
                                  ${analysis.score * 100}%, 
                                  ${theme.palette.action.hover} 0
                                )`,
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  width: '90%',
                                  height: '90%',
                                  borderRadius: '50%',
                                  backgroundColor: theme.palette.background.paper,
                                }
                              }}
                            >
                              <Stack 
                                alignItems="center" 
                                sx={{ 
                                  position: 'relative', 
                                  zIndex: 1,
                                  p: 2,
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Typography 
                                  variant="h3" 
                                  fontWeight="600"
                                  color={analysis.score >= 0.7 ? 'success.main' : 
                                         analysis.score >= 0.4 ? 'warning.main' : 
                                         'error.main'}
                                >
                                  {(analysis.score * 100).toFixed(0)}%
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary"
                                  sx={{ mt: -0.5 }}
                                >
                                  ATS Score
                                </Typography>
                              </Stack>
                            </Box>
                            <Stack spacing={1} sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight="600">
                                Resume Match Score
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {analysis.score >= 0.7 ? 'Strong match with the job requirements' :
                                 analysis.score >= 0.4 ? 'Moderate match with the job requirements' :
                                 'Low match with the job requirements'}
                              </Typography>
                              <Box sx={{ 
                                position: 'relative',
                                height: 12,
                                bgcolor: 'action.hover',
                                borderRadius: 6,
                                overflow: 'hidden',
                                mt: 1
                              }}>
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    height: '100%',
                                    width: `${analysis.score * 100}%`,
                                    bgcolor: analysis.score >= 0.7 ? 'success.main' : 
                                            analysis.score >= 0.4 ? 'warning.main' : 
                                            'error.main',
                                    borderRadius: 6,
                                    transition: 'width 0.5s ease-in-out',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  }}
                                />
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 1,
                                  }}
                                >
                                  {[0, 25, 50, 75, 100].map((value) => (
                                    <Box
                                      key={value}
                                      sx={{
                                        width: 2,
                                        height: 8,
                                        bgcolor: 'background.paper',
                                        borderRadius: 1,
                                        opacity: 0.5,
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                              <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Poor Match
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Strong Match
                                </Typography>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Box>

                  {analysis.score < 0.4 && (
                        <Alert 
                          severity="warning" 
                          sx={{ mb: 3 }}
                          icon={<WarningIcon />}
                        >
                          Your current experience shows a significant mismatch with the target role. 
                      The system may not be able to effectively optimize your resume for this position.
                    </Alert>
                  )}

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                              <AutoFixHighIcon color="primary" />
                              <Typography variant="h6" fontWeight="600">
                                Suggestions
                              </Typography>
                            </Stack>
                            <Stack spacing={1}>
                        {analysis.suggestions.map((suggestion: string, index: number) => (
                                <Chip
                                  key={index}
                                  label={suggestion}
                                  icon={<CheckCircleIcon />}
                                  variant="outlined"
                                  color="primary"
                                  sx={{ justifyContent: 'flex-start' }}
                                />
                              ))}
                            </Stack>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                              <AutoFixHighIcon color="primary" />
                              <Typography variant="h6" fontWeight="600">
                                Improvements
                              </Typography>
                            </Stack>
                            <Stack spacing={1}>
                        {analysis.improvements.map((improvement: string, index: number) => (
                                <Chip
                                  key={index}
                                  label={improvement}
                                  icon={<CheckCircleIcon />}
                                  variant="outlined"
                                  color="primary"
                                  sx={{ justifyContent: 'flex-start' }}
                                />
                              ))}
                            </Stack>
                          </Paper>
                        </Grid>
                      </Grid>

                  {analysis.score < 0.4 ? (
                        <Box sx={{ mt: 3 }}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Instead of optimizing your current resume, we recommend:
                        <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                          <li>Use the "Generate Experience" feature to create relevant experience points</li>
                          <li>Try a different role that better matches your current experience</li>
                          <li>Update your resume with relevant skills before optimization</li>
                        </ul>
                      </Alert>
                          <Button
                            fullWidth
                            variant="contained"
                            size="large"
                        onClick={() => navigate('/experience')}
                            startIcon={<WorkIcon />}
                            sx={{ py: 1.5 }}
                          >
                            Go to Generate Experience
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                      onClick={handleRewrite} 
                      disabled={loading}
                          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
                          sx={{ mt: 3, py: 1.5 }}
                    >
                          {loading ? 'Optimizing...' : 'Generate Optimized Resume'}
                        </Button>
                  )}
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Optimized Resume Section */}
              {optimizedResume && (
                <Grid item xs={12} ref={optimizedResumeRef}>
                  <Card elevation={0} sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                        <DescriptionIcon color="primary" />
                        <Typography variant="h5" fontWeight="600">
                          Optimized Resume
                        </Typography>
                      </Stack>

                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                            <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
                              Resume Text
                            </Typography>
                            <Box
                              component="pre"
                              sx={{
                                p: 2,
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                                overflow: 'auto',
                                maxHeight: '400px',
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                position: 'relative'
                              }}
                            >
                              <IconButton
                                onClick={handleCopyResume}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  bgcolor: 'background.paper',
                                  boxShadow: 1,
                                  '&:hover': {
                                    bgcolor: 'action.hover',
                                  }
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                              {optimizedResume.rewritten_resume}
                            </Box>
                          </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                              <AutoFixHighIcon color="primary" />
                              <Typography variant="h6" fontWeight="600">
                                Key Changes
                              </Typography>
                            </Stack>
                            <Stack spacing={1}>
                          {optimizedResume.key_changes.map((change: string, index: number) => (
                                <Chip
                                  key={index}
                                  label={change}
                                  icon={<CheckCircleIcon />}
                                  variant="outlined"
                                  color="primary"
                                  sx={{ justifyContent: 'flex-start' }}
                                />
                              ))}
                            </Stack>
                          </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                              <AssessmentIcon color="primary" />
                              <Typography variant="h6" fontWeight="600">
                                ATS Optimization
                              </Typography>
                            </Stack>
                            <Stack spacing={1}>
                          {optimizedResume.ats_optimization.map((optimization: string, index: number) => (
                                <Chip
                                  key={index}
                                  label={optimization}
                                  icon={<CheckCircleIcon />}
                                  variant="outlined"
                                  color="primary"
                                  sx={{ justifyContent: 'flex-start' }}
                                />
                              ))}
                            </Stack>
                          </Paper>
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            fullWidth
                            variant="contained"
                            size="large"
                        onClick={handleDownload}
                            startIcon={<DownloadIcon />}
                            sx={{ py: 1.5 }}
                          >
                            Download Optimized Resume (PDF)
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </>
          )}

          {/* Other routes */}
          {location.pathname === '/experience' && <GenerateExperience />}
          {location.pathname === '/jobs' && <JobSearch />}
          {location.pathname === '/job-management' && <JobManagement />}
          {location.pathname === '/cover-letter' && <CoverLetterGenerator />}
          {location.pathname === '/resume-history' && <ResumeHistory />}
          {location.pathname === '/settings' && <Settings />}
          {location.pathname === '/feedback' && <Feedback />}
        </Grid>
      </Box>

      <Snackbar 
        open={showOptimizedAlert} 
        autoHideDuration={6000} 
        onClose={() => setShowOptimizedAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowOptimizedAlert(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Your optimized resume is ready! You can now download it.
        </Alert>
      </Snackbar>

      <Snackbar 
        open={showCopyAlert} 
        autoHideDuration={3000} 
        onClose={() => setShowCopyAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowCopyAlert(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Resume text copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard; 